import React, { useEffect, useState } from "react";
import { getSettings } from "../apis/setting";

type HeaderMode = "no_header" | "default_header" | "custom_header_image";

export type PrintHeaderConfig = {
  mode: HeaderMode;
  noHeaderMarginMm: number;
  logoUrl: string | null;
  logoSizeMm: { w: number; h: number };
  showClinicName: boolean;
  showAddress: boolean;
  showPhone: boolean;
  clinicRegNumber: string | null;
  headerImageUrl: string | null;
  orgName: string;
  address: string;
  phone: string;
};

function toAbsoluteUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/${url.replace(/^\//, "")}`;
}

function loadConfigFromSettings(settings: any[]): PrintHeaderConfig {
  const user = (() => {
    try { return JSON.parse(window.localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();

  const find = (code: string) => settings.find((s) => s.code === code)?.value ?? null;

  const logoSizeRaw = find("prescription_logo_size");
  const [lw, lh] = (logoSizeRaw || "40x20").split("x").map(Number);

  const headerModeRaw = find("prescription_header");

  return {
    mode: (headerModeRaw as HeaderMode) || "default_header",
    noHeaderMarginMm: parseInt(find("prescription_no_header_margin") || "40", 10) || 40,
    logoUrl: find("prescription_logo") ? toAbsoluteUrl(find("prescription_logo")!) : null,
    logoSizeMm: { w: lw || 40, h: lh || 20 },
    showClinicName: find("prescription_show_clinic_name") !== "false",
    showAddress: find("prescription_show_address") !== "false",
    showPhone: find("prescription_show_phone") !== "false",
    clinicRegNumber: find("clinic_registration_number"),
    headerImageUrl: find("custom_header_image") ? toAbsoluteUrl(find("custom_header_image")!) : null,
    orgName: find("organization_name") || user?.activeClinic?.name || "",
    address: find("address") || user?.activeClinic?.address || "",
    phone: find("phone") || user?.activeClinic?.phone || "",
  };
}

type Props = {
  /** Pass pre-fetched config to skip the internal settings fetch */
  config?: PrintHeaderConfig;
};

const PrintHeader: React.FC<Props> = ({ config: externalConfig }) => {
  const [config, setConfig] = useState<PrintHeaderConfig | null>(externalConfig ?? null);

  useEffect(() => {
    if (externalConfig) { setConfig(externalConfig); return; }
    getSettings()
      .then((res: any) => {
        const settings: any[] = res?.data?.data || [];
        setConfig(loadConfigFromSettings(settings));
      })
      .catch(() => {});
  }, [externalConfig]);

  if (!config) return null;

  const { mode, noHeaderMarginMm, logoUrl, logoSizeMm, showClinicName, showAddress,
          showPhone, clinicRegNumber, headerImageUrl, orgName, address, phone } = config;

  if (mode === "no_header") {
    return <div style={{ height: `${noHeaderMarginMm}mm` }} />;
  }

  if (mode === "custom_header_image" && headerImageUrl) {
    return (
      <div style={{ borderBottom: "2px solid #e5e7eb", padding: "0 24px" }}>
        <img
          src={headerImageUrl}
          alt="Clinic header"
          style={{ width: "100%", maxHeight: 120, objectFit: "contain" }}
        />
      </div>
    );
  }

  const hasLeft = logoUrl || showClinicName || showAddress || showPhone;

  return (
    <div style={{ borderBottom: "2px solid #e5e7eb", padding: "16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {hasLeft && (
          <div>
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Clinic logo"
                style={{
                  width: logoSizeMm.w + "mm",
                  height: logoSizeMm.h + "mm",
                  objectFit: "contain",
                  display: "block",
                  marginBottom: (showClinicName || showAddress || showPhone) ? 6 : 0,
                }}
              />
            )}
            {showClinicName && orgName && (
              <div style={{ fontSize: 20, fontWeight: 700 }}>{orgName}</div>
            )}
            {showAddress && address && (
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>{address}</div>
            )}
            {showPhone && phone && (
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>☎ {phone}</div>
            )}
          </div>
        )}
        {clinicRegNumber && (
          <div style={{ textAlign: "right", color: "#6b7280", fontSize: 13, flexShrink: 0 }}>
            Reg. No. {clinicRegNumber}
          </div>
        )}
      </div>
    </div>
  );
};

export { loadConfigFromSettings };
export default PrintHeader;
